/**
 * ConversationComposerPanel — everything that sits above the thread's input: who is typing, the expired-session note,
 * the chosen slash-command chips and the slash-command results. Demo page: /chats (the strip above the composer).
 *
 * It renders inside the thread's composer, so it reads the typed text from the composer itself. The slash command is
 * derived from that text (`/strategy mean` is "search strategies for mean"); applying it clears the command text and
 * keeps the chosen options, which travel with the next message as its `commandContext`.
 */
import { useAui, useAuiState } from '@assistant-ui/react';
import { type ReactNode, useEffect, useRef } from 'react';
import {
  type ConversationCommandsActions,
  type ConversationCommandsState,
  detectSlashCommand,
} from '../../hooks/useConversationCommands.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { CommandChipStrip, ConversationCommandPanel } from './ConversationCommandPanel.js';
import type { SlashCommandKeys } from './useSlashCommandKeys.js';

export type ConversationCommands = ConversationCommandsState & ConversationCommandsActions;

interface ConversationComposerPanelProps {
  /** The runtime policy and intent routing: a row of their own above the input, so a narrow screen keeps its width. */
  tools?: ReactNode;
  /** Slash commands (detailed surface only); `null` where the surface has none. */
  commands: ConversationCommands | null;
  /** "Ada is typing…" or `null`. */
  typing: string | null;
  sessionExpired: { reason: string; instruction: string } | null;
  /** The slash panel's keyboard state (highlight, listed options, apply); `null` without commands. */
  slash?: SlashCommandKeys | null;
  /** First keystroke of a message: tell the others we are typing. */
  onKeystroke: () => void;
  /** The message was sent or the field emptied: tell the others we stopped. */
  onSendOrBlur: () => void;
}

export function ConversationComposerPanel({
  tools,
  commands,
  slash = null,
  typing,
  sessionExpired,
  onKeystroke,
  onSendOrBlur,
}: ConversationComposerPanelProps) {
  const text = useAuiState((s) => s.composer.text);
  const aui = useAui();
  const wasTyping = useRef(false);

  useEffect(() => {
    if (text.length > 0) {
      wasTyping.current = true;
      onKeystroke();
    } else if (wasTyping.current) {
      wasTyping.current = false;
      onSendOrBlur();
    }
  }, [text, onKeystroke, onSendOrBlur]);

  const command = commands ? detectSlashCommand(text) : null;

  // Applying keeps the chosen options and clears the command text. A research objective goes into the composer
  // itself, so it is the message Send posts.
  const applyCommand = () => {
    if (!commands || !command) return;
    if (command.command === 'research' && command.rest.trim()) {
      commands.captureResearchObjective(command.rest.trim());
      aui.composer().setText(command.rest.trim());
      return;
    }
    aui.composer().setText('');
  };

  // The panel's keys (Esc, Ctrl+Enter, Enter on /research) run the same action.
  const applyRef = slash?.applyRef;
  useEffect(() => {
    if (!applyRef) return;
    applyRef.current = applyCommand;
    return () => {
      applyRef.current = null;
    };
  });

  const resetHighlight = slash?.resetHighlight;
  const searchText = command?.rest;
  useEffect(() => {
    if (searchText !== undefined) resetHighlight?.();
  }, [searchText, resetHighlight]);

  // A research objective that is chosen while the composer is empty becomes the message to send.
  const researchObjective = commands?.researchObjective ?? null;
  const lastObjective = useRef<string | null>(null);
  useEffect(() => {
    if (researchObjective && researchObjective !== lastObjective.current && text.length === 0) {
      aui.composer().setText(researchObjective);
    }
    lastObjective.current = researchObjective;
  }, [researchObjective, text, aui]);

  const hasChips = commands !== null && (commands.selections.size > 0 || Boolean(commands.researchObjective));

  return (
    <>
      {tools && <div className="flex flex-wrap items-center gap-2">{tools}</div>}
      {typing && <p className="px-1 text-xs italic text-muted">{typing}</p>}
      {sessionExpired && (
        <Callout tone="warning" title="Session expired">
          {sessionExpired.instruction}
        </Callout>
      )}
      {commands && hasChips && (
        <CommandChipStrip
          selections={commands.selections}
          researchObjective={commands.researchObjective}
          onRemoveSelection={commands.removeSelection}
          onRemoveResearch={commands.removeResearchObjective}
          onClearAll={commands.clearAll}
        />
      )}
      {commands && command && (
        <div className="space-y-2">
          <ConversationCommandPanel
            activeCommand={command.command}
            searchQuery={command.command === 'research' ? '' : command.rest}
            selections={commands.selections}
            highlightIndex={slash?.highlightIndex ?? 0}
            onToggle={commands.toggleSelection}
            onOptionsChange={slash?.onOptionsChange}
          />
          <div className="flex justify-end">
            <Button size="sm" variant="secondary" onClick={applyCommand}>
              Apply to message
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
